import React from 'react'
import { roundWithLocale } from '@sketch/utils'
import copy, { getBorderWidthCopyValue } from './copy'
import BorderOptionsSection from './BorderOptionsSection'
import {
  AttributeList,
  FullCopyAttribute,
  Header,
  SubTitle,
  Color as ColorComponent,
  GradientAttribute,
} from '../../../components'
import { getDirtyAttributes } from '../../../components/Style/DirtyIconTooltip'
import {
  Border,
  BorderOptions,
  FillType,
  Gradient,
  Color,
} from '../../../../../../../inspector'
import { ColorFormat } from '../../../../../../types'
import { Flex } from '@sketch/components'
import {
  AllSidesIcon,
  BottomSideIcon,
  LeftSideIcon,
  PerSideBorderContainer,
  PerSideValue,
  RightSideIcon,
  TopSideIcon,
  Capitalized,
} from './Borders.styles'

export interface BordersProps {
  borders: Border[]
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  borderOptions?: BorderOptions
  originalValues?: Border[]
  originalValuesOptions?: BorderOptions
}

const isBlendModeVisible = (blendMode: string) =>
  blendMode.toUpperCase() !== 'NORMAL'

interface renderBorderProps extends Border {
  originalValue?: Border
}

const renderBorder = (
  {
    isEnabled,
    fillType,
    gradient,
    color,
    position,
    thickness,
    appearance,
    originalValue,
    sidesThickness,
  }: renderBorderProps,
  {
    onColorFormatChange,
    colorFormat,
  }: {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  },
  i: number
) => {
  if (!isEnabled) {
    return null
  }

  const sideValues = sidesThickness
    ? [
        sidesThickness.left,
        sidesThickness.top,
        sidesThickness.right,
        sidesThickness.bottom,
      ]
    : []

  const nonZeroSides = sideValues.filter(value => value !== 0)
  const hasSingleSideThickness = nonZeroSides.length === 1
  const widthCopyValue = getBorderWidthCopyValue({ thickness, sidesThickness })
  const widthDirtyAttributes = getDirtyAttributes({
    originalValueKey: originalValue?.sidesThickness
      ? 'sidesThickness'
      : 'thickness',
    valueToDisplay: originalValue
      ? getBorderWidthCopyValue(originalValue)
      : undefined,
    originalValues: originalValue,
    labelToDisplay: 'Width',
  })

  const blendMode = appearance && appearance.blendMode
  let colorOrGradient = null
  if (fillType === FillType.Color && color) {
    colorOrGradient = (
      <ColorComponent
        {...color}
        onColorFormatChange={onColorFormatChange}
        colorFormat={colorFormat}
        dirtyAttributes={
          originalValue?.color
            ? {
                originalValue: originalValue?.color as Color,
                originalProperty: 'color',
              }
            : undefined
        }
      />
    )
  } else if (fillType === FillType.Gradient && gradient) {
    colorOrGradient = (
      <GradientAttribute
        gradient={gradient}
        onColorFormatChange={onColorFormatChange}
        colorFormat={colorFormat}
        dirtyAttributes={
          originalValue?.gradient
            ? {
                originalGradientValue: originalValue?.gradient as Gradient,
              }
            : undefined
        }
      />
    )
  }

  return (
    <AttributeList key={`InspectorBorder${i}`} data-testid="border">
      {colorOrGradient}
      {isBlendModeVisible(blendMode) ? (
        <FullCopyAttribute
          label="Blend Mode"
          value={<Capitalized>{blendMode}</Capitalized>}
          copyValue={blendMode}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'blendMode',
            valueToDisplay: originalValue?.appearance.blendMode,
            originalValues: originalValue,
            labelToDisplay: 'Blend Mode',
          })}
        />
      ) : null}
      {position ? (
        <FullCopyAttribute
          label="Position"
          value={<Capitalized>{position}</Capitalized>}
          copyValue={position}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'position',
            valueToDisplay: originalValue?.position,
            originalValues: originalValue,
          })}
        />
      ) : null}
      {sidesThickness ? (
        <FullCopyAttribute
          label="Width"
          value={
            <PerSideBorderContainer>
              {(!hasSingleSideThickness || sidesThickness.left > 0) && (
                <PerSideValue $dimmed={sidesThickness.left === 0}>
                  <LeftSideIcon />
                  {`${roundWithLocale(sidesThickness.left, 2)}px`}
                </PerSideValue>
              )}
              {(!hasSingleSideThickness || sidesThickness.top > 0) && (
                <PerSideValue $dimmed={sidesThickness.top === 0}>
                  <TopSideIcon />
                  {`${roundWithLocale(sidesThickness.top, 2)}px`}
                </PerSideValue>
              )}
              {(!hasSingleSideThickness || sidesThickness.right > 0) && (
                <PerSideValue $dimmed={sidesThickness.right === 0}>
                  <RightSideIcon />
                  {`${roundWithLocale(sidesThickness.right, 2)}px`}
                </PerSideValue>
              )}
              {(!hasSingleSideThickness || sidesThickness.bottom > 0) && (
                <PerSideValue $dimmed={sidesThickness.bottom === 0}>
                  <BottomSideIcon />
                  {`${roundWithLocale(sidesThickness.bottom, 2)}px`}
                </PerSideValue>
              )}
            </PerSideBorderContainer>
          }
          copyValue={widthCopyValue}
          dirtyAttributes={widthDirtyAttributes}
        />
      ) : (
        <FullCopyAttribute
          label="Width"
          value={
            <Flex alignItems="center">
              <AllSidesIcon />
              {`${roundWithLocale(thickness, 2)}px`}
            </Flex>
          }
          copyValue={widthCopyValue}
          dirtyAttributes={widthDirtyAttributes}
        />
      )}
    </AttributeList>
  )
}

export const Borders = ({
  borders,
  onColorFormatChange,
  colorFormat,
  borderOptions,
  originalValues,
  originalValuesOptions,
}: BordersProps) => {
  const renderedBorders: Array<React.ReactElement> = []
  const reversedBorders = [...borders].reverse()
  const reversedOriginalValues = originalValues
    ? [...originalValues].reverse()
    : undefined

  const dirtyHeaderAttributes =
    originalValues && originalValues[0] === null
      ? { originalValue: 'disabled', originalProperty: 'Borders value' }
      : undefined

  reversedBorders.forEach((border, index) => {
    const renderedBorder = renderBorder(
      {
        ...border,
        originalValue: reversedOriginalValues
          ? reversedOriginalValues[index]
          : undefined,
      },
      {
        onColorFormatChange,
        colorFormat,
      },
      index
    )
    renderedBorder && renderedBorders.push(renderedBorder)
  })

  const sectionCopyValue = copy(reversedBorders, colorFormat)

  return renderedBorders.length > 0 ? (
    <>
      <Header
        copyValue={sectionCopyValue}
        dirtyAttributes={dirtyHeaderAttributes}
      >
        <SubTitle>Borders</SubTitle>
      </Header>
      {renderedBorders}
      {borderOptions && (
        <BorderOptionsSection
          borderOptions={borderOptions}
          originalValues={originalValuesOptions}
        />
      )}
    </>
  ) : null
}
